import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { enqueueSnackbar } from "notistack";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@components/ui/button";
import { forgotPassword } from "@/services/auth.service";
import { Loader2 } from "lucide-react";
import { getErrorFromBackend } from "@/utils/errors";

type Inputs = {
  email: string;
};

const forgotPasswordSchema = z.object({
  email: z
    .string({ required_error: "Email is required" })
    .trim()
    .min(1, "Email is required")
    .email("Invalid email address"),
});


const ForgotPasswordForm = () => {

  const methods = useForm<Inputs>({
    resolver: zodResolver(forgotPasswordSchema),
  });
  const { handleSubmit, control, formState } = methods;
  const { isSubmitting } = formState

  const onSubmit: SubmitHandler<Inputs> = async ({ email }) => {
    try {
      const message = await forgotPassword(email);
      enqueueSnackbar(message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    }
  };

  return (
    <Form {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-6">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-gray-800">Email <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    {...field}
                    className="text-slate-900"
                  />
                </FormControl>
                <FormMessage className="text-xs text-red-500"/>
              </FormItem>
            )}
          />
        </div>

        <Button disabled={isSubmitting} className="w-full" type="submit">
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Code"}
        </Button>
      </form>
    </Form>
  );
};

export default ForgotPasswordForm;
